import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { useState, type FC } from 'react';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import BucketIcon from 'components/atoms/icons/bucketIcon';
import BaseLoader from 'components/atoms/base-loader';
import AddMedicationForm from 'components/molecules/admin-panel/forms/medication';
import BasePagination from 'components/atoms/base-pagination';
import BaseSelectBox from 'components/atoms/base-select-box';
import PlusIcon from 'components/atoms/icons/plus';
import { isCombinedModule } from 'infrastructure/functions';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { Tabs } from 'antd';
import { DownOutlined, RightOutlined, UploadOutlined } from '@ant-design/icons';

import s from './styles.module.scss';
import MedicationItem from './components/medication-item';
import PapCaseList from './components/pap-case-list';
import { SORT_OPTIONS, useMedication } from './hooks/use-medication';
import { usePapCases } from './hooks/use-pap-cases';

import type { PapCasesSortBy, PapCasesSortOrder } from './types';
import type { ModuleType } from 'infrastructure/enums';

interface PatientMedicationProps {
  patientGuid: string;
  module: ModuleType;
}

const PatientMedication: FC<PatientMedicationProps> = ({
  patientGuid,
  module,
}) => {
  const { t } = useTranslation();
  const [papPage, setPapPage] = useState(1);
  const [papSortBy, setPapSortBy] = useState<PapCasesSortBy>('updatedAt');
  const [papSortOrder, setPapSortOrder] = useState<PapCasesSortOrder>('DESC');
  const {
    modalTitle,
    Alert,
    open,
    modifyItemGuid,
    medication,
    isLoading,
    isFetching,
    addLoading,
    editLoading,
    deleteLoading,
    loading,
    patientInfo,
    disableButton,
    wrapperRef,
    OPTIONS,
    status,
    page,
    itemPerPage,
    medicationForUpdate,
    documentationTimeSort,
    setDocumentationTimeSort,
    onCancelForm,
    handleDelete,
    handleEdit,
    scrollToTop,
    onSubmit,
    setStatus,
    setPage,
    setItemPerPage,
    toggleOpen,
    remove,
  } = useMedication({ patientGuid, module });
  const {
    papCases,
    count: papCasesCount,
    isLoading: papCasesLoading,
    isFetching: papCasesFetching,
    downloadPapForm,
  } = usePapCases(patientGuid, {
    page: papPage,
    itemsPerPage: 5,
    sortBy: papSortBy,
    sortOrder: papSortOrder,
  });
  const { isReadOnlyRole } = useCheckRoleAccess();
  const [isExpanded, setIsExpanded] = useState(true);

  const disableActions = isCombinedModule(module)
    ? isReadOnlyRole
    : disableButton;

  const medicationTabContent = (
    <>
      <div className={s['tab-header']}>
        <div className={s['header-select']}>
          <BaseSelectBox
            value={status}
            onChange={(v) => {
              remove();
              setPage(1);
              setStatus(v);
            }}
            options={OPTIONS}
            minWidth="110px"
          />
        </div>
        <div className={s['header-select']}>
          <BaseSelectBox
            value={documentationTimeSort || undefined}
            onChange={(v) => {
              setDocumentationTimeSort(v ?? '');
            }}
            options={SORT_OPTIONS}
            minWidth="110px"
          />
        </div>
        <BaseButton
          type="secondary"
          icon={<UploadOutlined />}
          label={t('labels.importFromEHR')}
          disabled={disableActions || loading}
        />
        <BaseButton
          dataCy="addMedication-button"
          icon={<PlusIcon color="transparent" />}
          label={`${t('controls.addMedication')}`}
          onClick={toggleOpen}
          loading={addLoading}
          disabled={disableActions || loading}
        />
      </div>
      <BaseLoader loading={isLoading || isFetching}>
        <div className={s.body} style={{ position: 'relative' }}>
          {!!medication?.items.length && (
            <div className={s['items-wrapper']}>
              {medication?.items.map((item) => {
                return (
                  <MedicationItem
                    key={item.guid}
                    item={item}
                    disableActions={disableActions}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    loading={deleteLoading || editLoading}
                    modifyItemGuid={modifyItemGuid}
                  />
                );
              })}
            </div>
          )}
          {medication?.items.length === 0 && (
            <EmptyTablePlaceholder
              text={t('labels.medicationHistoryIsEmpty')}
              icon={<BucketIcon width={32} height={32} />}
            />
          )}
          <BasePagination
            page={page}
            total={medication?.count}
            align="end"
            pageSizeOptions={[5, 10, 15, 20, 25, 30, 100]}
            onChange={(newPage) => {
              scrollToTop();
              setPage(newPage);
            }}
            pageSize={itemPerPage}
            onShowSizeChange={(current, size) => {
              scrollToTop();
              setItemPerPage(size);
            }}
            hide={(medication?.count ?? 0) < 5}
          />
        </div>
      </BaseLoader>
    </>
  );

  const tabItems = [
    {
      key: 'medication',
      label: t('labels.medication'),
      children: medicationTabContent,
    },
    {
      key: 'medication-history',
      label: t('labels.medicationHistory'),
      children: (
        <EmptyTablePlaceholder
          text={t('labels.medicationHistoryIsEmpty')}
          icon={<BucketIcon width={32} height={32} />}
        />
      ),
    },
    {
      key: 'prescription',
      label: t('labels.prescriptionAssistanceProgram_PAP'),
      children: (
        <PapCaseList
          items={papCases}
          loading={papCasesLoading || papCasesFetching}
          onDownloadPapForm={downloadPapForm}
          page={papPage}
          total={papCasesCount}
          onPageChange={setPapPage}
          sortBy={papSortBy}
          sortOrder={papSortOrder}
          onSortChange={(sortBy, sortOrder) => {
            setPapSortBy(sortBy);
            setPapSortOrder(sortOrder);
            setPapPage(1);
          }}
          patientGuid={patientGuid}
          patientInfo={patientInfo}
        />
      ),
    },
  ];

  return (
    <>
      <div className={s.wrapper} ref={wrapperRef} id="medication">
        <div className={s.header}>
          <BaseButton
            type="secondary"
            className={s['toggle-button']}
            icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
          />
          <h4 className={s.title}>{t('labels.medications')}</h4>
        </div>
        {isExpanded && (
          <Tabs
            defaultActiveKey="medication"
            items={tabItems}
            className={s.tabs}
          />
        )}
      </div>

      <AddMedicationForm
        loading={addLoading || editLoading}
        onSubmit={onSubmit}
        onCancelForm={onCancelForm}
        defaultValues={medicationForUpdate}
        modalTitle={modalTitle}
        open={open}
      />
      {Alert}
    </>
  );
};
export default PatientMedication;
